import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { CreditCard, Shield, Eye, EyeOff, RefreshCw, CheckCircle, Save } from 'lucide-react';
import { adminAPI } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const AdminPayments = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [form, setForm] = useState({
    mp_public_key: '',
    mp_access_token: '',
    mp_client_id: '',
    mp_client_secret: '',
    is_mp_active: false,
    is_card_active: true,
    is_wallet_active: true,
    is_transfer_active: true,
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getMPConfig();
      setConfig(data);
      setForm(prev => ({
        ...prev,
        mp_public_key: data.mp_public_key || '',
        mp_client_id: data.mp_client_id || '',
        is_mp_active: data.is_mp_active || false,
        is_card_active: data.is_card_active !== false,
        is_wallet_active: data.is_wallet_active !== false,
        is_transfer_active: data.is_transfer_active !== false,
      }));
    } catch {
      toast({ title: 'Error cargando configuracion', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.mp_access_token) delete payload.mp_access_token;
      if (!payload.mp_client_secret) delete payload.mp_client_secret;
      await adminAPI.updateMPConfig(payload);
      toast({ title: 'Configuracion guardada' });
      setForm(prev => ({ ...prev, mp_access_token: '', mp_client_secret: '' }));
      load();
    } catch {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-500/20">
            <CreditCard className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="admin-payments-title">Configuracion de Pagos</h1>
            <p className="text-gray-500 text-sm">MercadoPago - Credenciales y estado</p>
          </div>
        </div>

        <Card className="bg-gray-800 border-gray-700 p-6 space-y-6" data-testid="mp-config-form">
          {/* Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-700/50">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-white font-medium">MercadoPago activo</p>
                <p className="text-gray-400 text-xs">Habilitar pagos con MercadoPago</p>
              </div>
            </div>
            <Switch
              checked={form.is_mp_active}
              onCheckedChange={(v) => setForm(prev => ({ ...prev, is_mp_active: v }))}
              data-testid="mp-toggle"
            />
          </div>

          {/* Public Key */}
          <div className="space-y-2">
            <Label className="text-gray-300">Public Key</Label>
            <Input
              value={form.mp_public_key}
              onChange={(e) => setForm(prev => ({ ...prev, mp_public_key: e.target.value }))}
              placeholder="APP_USR-..."
              className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
              data-testid="mp-public-key"
            />
          </div>

          {/* Access Token */}
          <div className="space-y-2">
            <Label className="text-gray-300">Access Token</Label>
            <div className="relative">
              <Input
                type={showToken ? 'text' : 'password'}
                value={form.mp_access_token}
                onChange={(e) => setForm(prev => ({ ...prev, mp_access_token: e.target.value }))}
                placeholder={config?.mp_access_token_set ? '••••••• (configurado)' : 'APP_USR-...'}
                className="bg-gray-700 border-gray-600 text-white font-mono text-sm pr-10"
                data-testid="mp-access-token"
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {config?.mp_access_token_set && (
              <p className="text-xs text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Token configurado</p>
            )}
          </div>

          {/* Client ID */}
          <div className="space-y-2">
            <Label className="text-gray-300">Client ID</Label>
            <Input
              value={form.mp_client_id}
              onChange={(e) => setForm(prev => ({ ...prev, mp_client_id: e.target.value }))}
              placeholder="Client ID"
              className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
              data-testid="mp-client-id"
            />
          </div>

          {/* Client Secret */}
          <div className="space-y-2">
            <Label className="text-gray-300">Client Secret</Label>
            <div className="relative">
              <Input
                type={showSecret ? 'text' : 'password'}
                value={form.mp_client_secret}
                onChange={(e) => setForm(prev => ({ ...prev, mp_client_secret: e.target.value }))}
                placeholder={config?.mp_client_secret_set ? '••••••• (configurado)' : 'Secret'}
                className="bg-gray-700 border-gray-600 text-white font-mono text-sm pr-10"
                data-testid="mp-client-secret"
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Payment Method Toggles */}
          <div className="border-t border-gray-600 pt-6">
            <h3 className="text-white font-semibold mb-4">Metodos de pago habilitados</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-700/50">
                <div>
                  <p className="text-white text-sm font-medium">Tarjeta (credito/debito)</p>
                  <p className="text-gray-400 text-xs">Via MercadoPago Bricks</p>
                </div>
                <Switch checked={form.is_card_active}
                  onCheckedChange={(v) => setForm(prev => ({ ...prev, is_card_active: v }))}
                  data-testid="toggle-card" />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-700/50">
                <div>
                  <p className="text-white text-sm font-medium">Billetera Digital</p>
                  <p className="text-gray-400 text-xs">Pago con saldo del usuario</p>
                </div>
                <Switch checked={form.is_wallet_active}
                  onCheckedChange={(v) => setForm(prev => ({ ...prev, is_wallet_active: v }))}
                  data-testid="toggle-wallet" />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-700/50">
                <div>
                  <p className="text-white text-sm font-medium">Transferencia bancaria</p>
                  <p className="text-gray-400 text-xs">Persona a persona (P2P)</p>
                </div>
                <Switch checked={form.is_transfer_active}
                  onCheckedChange={(v) => setForm(prev => ({ ...prev, is_transfer_active: v }))}
                  data-testid="toggle-transfer" />
              </div>
            </div>
          </div>

          <Button
            onClick={save}
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="mp-save-btn"
          >
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar configuracion
          </Button>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminPayments;
